/**
 * Virtual expansion for recurring activities.
 *
 * Recurrence schedules are expanded client-side into virtual Activity objects.
 * Virtual activities are display-only until the user interacts (mark done, edit, etc.),
 * at which point they are materialized into real DB rows via the API.
 */

import { RRule } from 'rrule';
import type { Activity, RecurrenceSchedule } from '../api/client';

export interface VirtualActivity extends Activity {
  _isVirtual: true;
  _scheduleId: string;
  _occurrenceTime: string; // ISO datetime
}

/** Type guard for virtual activities. */
export function isVirtualActivity(activity: Activity | VirtualActivity): activity is VirtualActivity {
  return '_isVirtual' in activity && activity._isVirtual === true;
}

/** Generate a deterministic ID for a virtual occurrence. */
function virtualId(scheduleId: string, dateStr: string): string {
  return `virtual-${scheduleId}-${dateStr}`;
}

/** Format a Date as YYYY-MM-DD in local timezone. */
function formatDateLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/*
 * rrule expands in UTC, so a schedule made at 09:00 EDT (13:00Z) keeps
 * producing 13:00Z: 08:00 local after DST ends, and a different local date
 * for times near midnight. Recurrences are wall-clock times (RFC 5545), so
 * expand in "floating" time, as rrule's README prescribes: hand rrule a UTC
 * date whose UTC fields are the local wall-clock fields, then map each result
 * back. The wall clock is the device's current timezone.
 */
function toFloating(d: Date): Date {
  return new Date(Date.UTC(
    d.getFullYear(), d.getMonth(), d.getDate(),
    d.getHours(), d.getMinutes(), d.getSeconds(), d.getMilliseconds(),
  ));
}

function fromFloating(d: Date): Date {
  return new Date(
    d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(),
    d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds(), d.getUTCMilliseconds(),
  );
}

/** The schedule's rule, anchored at its start in floating time; null if unparseable. */
function scheduleRule(schedule: RecurrenceSchedule): RRule | null {
  try {
    const parsed = RRule.fromString(schedule.rrule);
    return new RRule({
      ...parsed.origOptions,
      dtstart: toFloating(new Date(schedule.schedule_start)),
    });
  } catch {
    return null;
  }
}

/**
 * Expand a single recurrence schedule into virtual activity occurrences
 * for the given date range.
 *
 * @param schedule - The recurrence schedule with RRULE
 * @param template - The template activity (source for encrypted fields)
 * @param rangeStart - Start of expansion window (inclusive)
 * @param rangeEnd - End of expansion window (inclusive)
 * @param existingActivities - Real activities to dedup against
 */
export function expandScheduleOccurrences(
  schedule: RecurrenceSchedule,
  template: Activity,
  rangeStart: Date,
  rangeEnd: Date,
  existingActivities: Activity[],
): VirtualActivity[] {
  if (!schedule.is_active) return [];

  // Build dedup set: dates that already have a real activity for this schedule
  const existingDates = new Set<string>();
  for (const a of existingActivities) {
    if (
      a.recurrence_schedule_id === schedule.id ||
      a.exception_from_schedule_id === schedule.id
    ) {
      existingDates.add(formatDateLocal(new Date(a.start_time)));
    }
  }

  const rule = scheduleRule(schedule);
  if (!rule) return [];

  // Respect schedule_end
  const effectiveEnd = schedule.schedule_end
    ? new Date(Math.min(rangeEnd.getTime(), new Date(schedule.schedule_end).getTime()))
    : rangeEnd;

  const occurrences = rule
    .between(toFloating(rangeStart), toFloating(effectiveEnd), true)
    .map(fromFloating);

  const virtuals: VirtualActivity[] = [];
  for (const occ of occurrences) {
    const dateStr = formatDateLocal(occ);

    // Skip if a real activity already exists for this date
    if (existingDates.has(dateStr)) continue;

    virtuals.push({
      id: virtualId(schedule.id, dateStr),
      user_id: template.user_id,
      team_id: template.team_id,
      key_version: template.key_version,
      created_by_user_id: template.created_by_user_id,
      goal_id: template.goal_id,
      recurrence_schedule_id: schedule.id,
      is_exception: false,
      exception_from_schedule_id: null,
      title_encrypted: template.title_encrypted,
      description_encrypted: template.description_encrypted,
      encryption_metadata: template.encryption_metadata,
      status: 'todo',
      estimated_minutes_encrypted: template.estimated_minutes_encrypted,
      start_time: occ.toISOString(),
      completed_at: null,
      sort_order: template.sort_order,
      created_at: template.created_at,
      updated_at: null,
      _isVirtual: true,
      _scheduleId: schedule.id,
      _occurrenceTime: occ.toISOString(),
    });
  }

  return virtuals;
}

/**
 * Find the most recent missed occurrence for a schedule that falls before today
 * and has not been materialized into a real activity.
 *
 * This catches low-frequency schedules (monthly, quarterly, yearly) whose missed
 * occurrences fall outside the normal 14-day lookback window.
 */
export function findMostRecentMissedOccurrence(
  schedule: RecurrenceSchedule,
  template: Activity,
  existingActivities: Activity[],
): VirtualActivity | null {
  if (!schedule.is_active) return null;

  const rule = scheduleRule(schedule);
  if (!rule) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const effectiveEnd = schedule.schedule_end
    ? new Date(Math.min(today.getTime(), new Date(schedule.schedule_end).getTime()))
    : today;

  if (effectiveEnd < new Date(schedule.schedule_start)) return null;

  const floatingLast = rule.before(toFloating(effectiveEnd), true);
  if (!floatingLast) return null;
  const lastOcc = fromFloating(floatingLast);

  // Build dedup set
  const existingDates = new Set<string>();
  for (const a of existingActivities) {
    if (
      a.recurrence_schedule_id === schedule.id ||
      a.exception_from_schedule_id === schedule.id
    ) {
      existingDates.add(formatDateLocal(new Date(a.start_time)));
    }
  }

  const dateStr = formatDateLocal(lastOcc);
  if (existingDates.has(dateStr)) return null;

  return {
    id: virtualId(schedule.id, dateStr),
    user_id: template.user_id,
    team_id: template.team_id,
    key_version: template.key_version,
    created_by_user_id: template.created_by_user_id,
    goal_id: template.goal_id,
    recurrence_schedule_id: schedule.id,
    is_exception: false,
    exception_from_schedule_id: null,
    title_encrypted: template.title_encrypted,
    description_encrypted: template.description_encrypted,
    encryption_metadata: template.encryption_metadata,
    status: 'todo',
    estimated_minutes_encrypted: template.estimated_minutes_encrypted,
    start_time: lastOcc.toISOString(),
    completed_at: null,
    sort_order: template.sort_order,
    created_at: template.created_at,
    updated_at: null,
    _isVirtual: true,
    _scheduleId: schedule.id,
    _occurrenceTime: lastOcc.toISOString(),
  };
}

/**
 * Expand all active schedules into virtual activities for a date range.
 * Includes a catch-up pass to surface missed occurrences beyond the normal range.
 *
 * @param schedules - Active recurrence schedules
 * @param activities - All real activities (used for dedup and finding templates)
 * @param rangeStart - Start of expansion window
 * @param rangeEnd - End of expansion window
 */
export function expandAllSchedules(
  schedules: RecurrenceSchedule[],
  activities: Activity[],
  rangeStart: Date,
  rangeEnd: Date,
): VirtualActivity[] {
  const allVirtuals: VirtualActivity[] = [];

  // Build a map of activity ID -> Activity for template lookup
  const activityMap = new Map<string, Activity>();
  for (const a of activities) {
    activityMap.set(a.id, a);
  }

  for (const schedule of schedules) {
    if (!schedule.activity_template_id) continue;

    // The template is excluded from the activities list endpoint,
    // so we fetch it separately. But if it happens to be in the map, use it.
    const template = activityMap.get(schedule.activity_template_id);
    if (!template) continue;

    const virtuals = expandScheduleOccurrences(
      schedule,
      template,
      rangeStart,
      rangeEnd,
      activities,
    );
    allVirtuals.push(...virtuals);
  }

  // Catch-up pass: surface the most recent missed occurrence per schedule
  // that falls outside the normal expansion range.
  const virtualIds = new Set(allVirtuals.map(v => v.id));
  for (const schedule of schedules) {
    if (!schedule.activity_template_id) continue;
    const template = activityMap.get(schedule.activity_template_id);
    if (!template) continue;

    const catchUp = findMostRecentMissedOccurrence(schedule, template, activities);
    if (catchUp && !virtualIds.has(catchUp.id)) {
      allVirtuals.push(catchUp);
    }
  }

  return allVirtuals;
}

/**
 * Merge real activities with virtual activities.
 * Virtual activities are appended; dedup is already handled during expansion.
 */
export function mergeRealAndVirtual(
  activities: Activity[],
  virtualActivities: VirtualActivity[],
): (Activity | VirtualActivity)[] {
  return [...activities, ...virtualActivities];
}

/**
 * Get the date range for the "This Week" tab expansion.
 * Past 14 days (for pending overdue) through end of current week (Saturday).
 */
export function getThisWeekRange(): { start: Date; end: Date } {
  const now = new Date();

  // 14 days ago for pending overdue
  const start = new Date(now);
  start.setDate(now.getDate() - 14);
  start.setHours(0, 0, 0, 0);

  // End of week (Saturday)
  const dayOfWeek = now.getDay(); // 0=Sun
  const daysUntilSat = 6 - dayOfWeek;
  const end = new Date(now);
  end.setDate(now.getDate() + daysUntilSat);
  end.setHours(23, 59, 59, 999);

  return { start, end };
}

/**
 * Get the date range for Upcoming tab expansion.
 * From day after end of this week through N weeks ahead.
 */
export function getUpcomingRange(weeksAhead: number = 4): { start: Date; end: Date } {
  const now = new Date();

  // Start: day after this Saturday
  const dayOfWeek = now.getDay();
  const daysUntilSat = 6 - dayOfWeek;
  const start = new Date(now);
  start.setDate(now.getDate() + daysUntilSat + 1);
  start.setHours(0, 0, 0, 0);

  // End: weeksAhead weeks from start
  const end = new Date(start);
  end.setDate(start.getDate() + weeksAhead * 7 - 1);
  end.setHours(23, 59, 59, 999);

  return { start, end };
}
